import { useCallback } from 'react';
import clock from '~/assets/icons/clock.svg';
import info from '~/assets/icons/info_round_2.svg';
import { CurrencyIcon } from '~/components/collections';
import { Button, ButtonColors, ButtonHeight } from '~/components/ui/Button';
import {
	NumberplatePreview,
	NumberPlatePreviewSize,
} from '~/components/ui/NumberplatePreview';
import { SectionHeading } from '~/components/ui/SectionHeading';
import { Text } from '~/components/ui/Text';
import { useWalletIcon } from '~/hooks';
import { rage, useObservable } from '~/rage';
import {
	Currency,
	ModalButtonColor,
	ModalType,
	numWord,
	WalletType,
} from '~/shared';
import { useLayoutSelector } from '../../redux';
import {
	StyledButtonContent,
	StyledContainer,
	StyledInfoContainer,
	StyledRow,
} from './VehicleInformation.styled';

const VehicleInformation: React.FC = () => {
	const tabIndex = useLayoutSelector((state) => state.plateBuying.tabIndex);

	const storeNumberPlatePrice =
		rage.vehicleNumberPlates.getStoreNumberPlatePrice();
	const storeNumberPlateMaxDays =
		rage.vehicleNumberPlates.getStoreNumberPlateMaxDays();
	const vehicleName = useObservable(rage.vehicles.getLocalVehicleName());
	const playerName = useObservable(rage.player.getNickname());
	const numberPlate = useObservable(
		rage.vehicleNumberPlates.getLocalOwnedVehicleNumberPlate(),
	);

	const donateIcon = useWalletIcon(Currency.Donate, true);

	const handleSave = useCallback(() => {
		let plateName = `${numberPlate?.text ?? ''}${numberPlate?.region ?? ''}`;
		if (plateName.length === 0) {
			plateName = '???';
		}

		rage.modal.showModal(
			{
				type: ModalType.Price,
				title: 'Подтверждение',
				text: `Вы действительно хотите сохранить номер ${plateName}?`,
				price: storeNumberPlatePrice,
				wallet: WalletType.Donate,
				fields: storeNumberPlateMaxDays
					? [
							{
								label: 'Срок хранения',
								value: String(storeNumberPlateMaxDays),
								valueSuffix: ` ${numWord(storeNumberPlateMaxDays, [
									'день',
									'дня',
									'дней',
								])}`,
								icon: clock,
								iconColor: '#ffbf42',
							},
					  ]
					: undefined,
				acceptButton: { text: 'Оплатить', color: ModalButtonColor.Success },
				cancelButton: {
					text: 'Отменить',
					color: ModalButtonColor.Secondary,
				},
			},
			(result) => {
				if (result.isAccepted) {
					rage.vehicleNumberPlates
						.moveNumberPlateToStorage()
						.then(() => rage.vehicleNumberPlates.exitMenu())
						.catch((err) => rage.notifications.showException(err));
				}
			},
		);
	}, [storeNumberPlatePrice, storeNumberPlateMaxDays, numberPlate]);

	return (
		<StyledContainer>
			<SectionHeading text={'Информация о ТС'} icon={info} />
			<StyledInfoContainer>
				<StyledRow>
					<Text>Владелец</Text>
					<Text>{playerName}</Text>
				</StyledRow>
				<StyledRow>
					<Text>Автомобиль</Text>
					<Text>{vehicleName}</Text>
				</StyledRow>
				<StyledRow>
					<Text>Текущий гос. номер</Text>
					<NumberplatePreview
						numberPlate={numberPlate}
						size={NumberPlatePreviewSize.Size20}
					/>
				</StyledRow>
			</StyledInfoContainer>
			{tabIndex === 1 && (
				<Button
					color={ButtonColors.Select2}
					height={ButtonHeight.Height50}
					onClick={handleSave}
					noInvertImage
				>
					<StyledButtonContent>
						<Text>Сохранить гос. номер за</Text>
						<CurrencyIcon icon={donateIcon} size="lg" asMask color="#FFD035" />
						<Text style={{ fontWeight: 700 }}>{storeNumberPlatePrice}</Text>
					</StyledButtonContent>
				</Button>
			)}
		</StyledContainer>
	);
};

export { VehicleInformation };
